/*
 * COPYRIGHT (c) 2022
 * HONEYWELL INC.,
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import {
  formattedDate,
  getFirstDayofSelectedMonth,
  isObjectOrArrayAndEmpty
} from '../../../shared/Utils/Utils';
import { getConnctorConfig } from '../../../shared/Utils/DataConnectorUtils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import ICarbonEmissionHOCProps from './ICarbonEmissionHOCProps';
import useMounted from '../../../shared/useMount/useMount';
import CarbonComponentSite from '../CarbonComponent/CarbonComponentSite';
import './CarbonEmissionHOC.scss';
import {
  ECarbonCartAt,
  IAny,
  ICarbonData,
  ISiteCard
} from '../../IEnergyManagementProps';
import { getCarbonEndDateBasedOnSub } from '../../../shared/Utils/CarbonViewDetailsUtils';
import TooltipError from '../../../../images/tooltipError.svg';
import { ApiContext } from '../../../../context/ApiContext';
import { Card, Tooltip } from '@scuf/common';
import { EmaasSubscriptionType } from '../../../shared/Enums';
import CarbonComponentSiteSeparate from '../CarbonComponent/CarbonComponentSiteSeparate';
import moment from 'moment';
interface ICarbonEmissionHOCContent extends ISiteCard {
  data: ICarbonData;
  toggleFeatureScopeOne: boolean | undefined;
  forGraph?: boolean;
  forSummary?: boolean;
  forTable?: boolean;
}

const DataAvailableCheck = (dataAvailableCheck: IAny) =>
  dataAvailableCheck ? true : false;

const CarbonEmissionHOCContent = (props: ICarbonEmissionHOCContent) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, toggleFeatureScopeOne } = props;

  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <CarbonComponentSiteSeparate
      data={data}
      toggleFeatureScopeOne={toggleFeatureScopeOne}
      forSummary={props.forSummary}
      forGraph={props.forGraph}
      forTable={props.forTable}
    />
  );
};

const CarbonEmmisionHOCWithLoader = withLoadingStatus(
  withErrorStatus(CarbonEmissionHOCContent)
);

const getConnectorConfigName = (toggleFeatureScopeOne: boolean | undefined) => {
  return toggleFeatureScopeOne
    ? 'site-carbon-new-summary'
    : 'Co2-carbonEmission-summary';
};

const CarbonEmissionHOCSiteSeparate = (props: ICarbonEmissionHOCProps) => {
  const [t] = useTranslation();
  const d = descriptors.energyManagement;
  const emaasContext = useContext(ApiContext);
  const { toggleFeatureScopeOne } = emaasContext!;
  const {
    bearerToken,
    requestContext,
    carbonApiFor,
    dataSourceType,
    dataSource
  } = props;

  const apiContext = useContext(ApiContext);

  const isSitePremium = apiContext.toggleSeparateApi;
  const startTime = getFirstDayofSelectedMonth(
    requestContext.context.startTime!
  );
  const endTime = requestContext.context.endTime!;

  const endTimeNewFormat = getCarbonEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!,
    dataSourceType
  );

  const connectorConfig =
    carbonApiFor === ECarbonCartAt.siteLabel
      ? getConnctorConfig(
          getConnectorConfigName(toggleFeatureScopeOne),
          requestContext,
          startTime,
          endTimeNewFormat,
          { dataSource: dataSourceType || '' }
        )
      : {
          name: getConnectorConfigName(toggleFeatureScopeOne),
          options: {
            startDate: startTime,
            endDate: endTimeNewFormat,
            scopeType: 'all',
            dataSource: dataSourceType || ''
          }
        };

  const connectorConfigPerformanceAggregation = {
    name: toggleFeatureScopeOne
      ? 'site-carbon-new-performance-aggregation'
      : 'Co2-carbonEmission-performance-aggregation',
    options: {
      startDate: startTime,
      endDate: endTimeNewFormat,
      scopeType: 'all',
      dataSource: dataSourceType || ''
    }
  };

  const connectorConfigTable = {
    name: toggleFeatureScopeOne
      ? 'site-carbon-new-table'
      : 'Co2-carbonEmission-table',
    options: {
      startDate: startTime,
      endDate: endTimeNewFormat,
      groupType: 'category',
      dataSource: dataSourceType || ''
    }
  };
  const isMounted = useMounted();

  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });

  let dataConnectorPerformanceAggregation: any;
  if (isSitePremium) {
    dataConnectorPerformanceAggregation = useConnectorAndDynamicProps({
      connectorConfig: connectorConfigPerformanceAggregation,
      requestContext,
      bearerToken
    })[0];
  }

  let dataConnectorTable: any;
  if (isSitePremium) {
    dataConnectorTable = useConnectorAndDynamicProps({
      connectorConfig: connectorConfigTable,
      requestContext,
      bearerToken
    })[0];
  }
  const connectorConfigMissigData = {
    name: 'get-missing-data-details-forPeriod',
    options: {
      startDate: startTime,
      endDate: endTime
    }
  };
  const [dataConnectorMissingData] = useConnectorAndDynamicProps({
    connectorConfig: connectorConfigMissigData,
    requestContext,
    bearerToken
  });

  useEffect(() => {
    if (isMounted) {
      //dataConnector?.actions?.getAll();
      onRefresh();
      onRefreshPerf();
      onRefreshTable();
      dataConnectorMissingData?.actions?.getAll();
    }
  }, [requestContext, dataSourceType]);

  const onRefreshPerf = () =>
    dataConnectorPerformanceAggregation &&
    dataConnectorPerformanceAggregation.actions &&
    dataConnectorPerformanceAggregation.actions.getAll();

  const onRefreshTable = () =>
    dataConnectorTable &&
    dataConnectorTable.actions &&
    dataConnectorTable.actions.getAll();

  const onRefresh = () => dataConnector?.actions?.getAll();
  const { all, loading, error, status } = dataConnector.data;

  const {
    all: allMissingData,
    loading: loadingMissingData,
    error: errorMissingData,
    status: statusMissingData
  } = dataConnectorMissingData.data;

  const allTable = dataConnectorTable.data.all;
  const loadingDataTable = dataConnectorTable.data.loading;
  const errorDataTable = dataConnectorTable.data.error;
  const statusDataTable = dataConnectorTable.data.status;

  const allPerf = dataConnectorPerformanceAggregation.data.all;
  const loadingPerf = dataConnectorPerformanceAggregation.data.loading;
  const errorPerf = dataConnectorPerformanceAggregation.data.error;
  const statusPerf = dataConnectorPerformanceAggregation.data.status;

  const MissingDataCondition = allMissingData as IAny;
  const isMissingDataAvailable = DataAvailableCheck(MissingDataCondition);

  const CarbonEmissionSavings = (all as unknown) as ICarbonData;

  const PerformanceData = (allPerf as unknown) as ICarbonData;
  const isDataAvailableForPerfromance =
    PerformanceData &&
    PerformanceData.monthWiseAggregations &&
    PerformanceData.monthWiseAggregations.length
      ? true
      : false;

  const TableData = (allTable as unknown) as ICarbonData;
  const isDataAvailableForTable =
    TableData && TableData.emissionsByUtility ? true : false;

  const electricitySavings = (all as unknown) as ICarbonData;
  const isDataAvailable =
    electricitySavings && electricitySavings.totalAggregations ? true : false;

  //const isDataAvailable = DataAvailableCheck(CarbonEmissionSavings);

  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(0);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as ICarbonData);

  const [noDataCount, setnoDataCount] = useState<number>(0);

  const [statusCodePerf, setStatusCodePerf] = useState<number | null>(0);
  const [dataPerf, setDataPerf] = useState({} as ICarbonData);
  const [isLoadingPerf, setIsLoadingPerf] = useState<boolean>(loadingPerf);
  const [errorInfoPerf, setErrorInfoPerf] = useState(errorPerf);

  const [tableData, setTableData] = useState({} as ICarbonData);
  const [loadingTable, setLoadingTable] = useState<boolean>(loadingDataTable);
  const [errorTable, setErrorTable] = useState(errorDataTable);
  const [statusCodeTable, setStatusCodeTable] = useState<number | null>(0);

  useEffect(() => {
    setIsLoadingPerf(loadingPerf);
    if (
      isDataAvailableForPerfromance ||
      errorPerf ||
      statusPerf ||
      !loadingPerf
    ) {
      if (isDataAvailableForPerfromance) {
        let tempData: IAny = JSON.parse(JSON.stringify(PerformanceData));
        setDataPerf(tempData);
      }
      setErrorInfoPerf(errorPerf);
      setStatusCodePerf(statusPerf);
    }
  }, [isDataAvailableForPerfromance, loadingPerf, statusPerf]);

  // useEffect(() => {
  //   setIsLoading(loading);
  //   if ((isDataAvailable || error || status) && !loading) {
  //     if (isDataAvailable) {
  //       setData(CarbonEmissionSavings);
  //     }
  //     setErrorInfo(error);
  //     setStatusCode(status);
  //   }
  // }, [isDataAvailable, loading, status]);

  useEffect(() => {
    setIsLoading(loading);

    if (isDataAvailable || error || status || !loading) {
      if (isDataAvailable) {
        let tempData = electricitySavings;
        setData(tempData);
      }
      setErrorInfo(error);
      setStatusCode(status);
    }
  }, [isDataAvailable, loading, status]);

  useEffect(() => {
    setLoadingTable(loadingDataTable);

    if (
      isDataAvailableForTable ||
      errorDataTable ||
      statusDataTable ||
      !loadingDataTable
    ) {
      if (isDataAvailableForTable) {
        let tempData: any = TableData;
        setTableData(tempData);
      }
      setErrorTable(errorDataTable);
      setStatusCodeTable(statusDataTable);
    }
  }, [isDataAvailableForTable, loadingDataTable, statusDataTable]);

  useEffect(() => {
    if (
      (isMissingDataAvailable || errorMissingData || statusMissingData) &&
      !loadingMissingData
    ) {
      setnoDataCount(MissingDataCondition.totalAlerts);
    }
  }, [isMissingDataAvailable, loadingMissingData, statusMissingData]);

  return (
    <>
      {carbonApiFor === ECarbonCartAt.siteLabel &&
        noDataCount > 0 &&
        toggleFeatureScopeOne && (
          <div className="errorToolTipDisplay">
            <Tooltip
              element={<img className="alarm-meter-icon" src={TooltipError} />}
              event="hover"
              content={`${t(d.DATANEEDEDCONTENT)}`}
              position="bottom center"
              size="small"
              className="dataNeeded"
            />
            {/* <img className="alarm-meter-icon" src={TooltipError} /> */}
            <div className="kpi-card-title-one">{t(d.DATANEEDED)}</div>
          </div>
        )}
      {statusCode == 204 || statusCodePerf == 204 || statusCodeTable == 204 ? (
        <Card.Content className="kpi-content-container">
          <CarbonEmmisionHOCWithLoader
            loading={isLoading}
            error={errorInfo}
            statusCode={204}
            onRefresh={onRefresh}
            data={data}
            loadingText={t(d.LOADING)}
            title={t(d.CONNECTIONERRORTITLE)}
            noDataMessage={t(d.NODATAAVAILABLE)}
            connectionErrorMessage={t(d.CONNECTIONERROR)}
            toggleFeatureScopeOne={toggleFeatureScopeOne}
            forSummary={false}
          />
        </Card.Content>
      ) : (
        <>
          <Card.Content className="kpi-content-container siteview-progressbar">
            <CarbonEmmisionHOCWithLoader
              loading={isLoading}
              error={errorInfo}
              statusCode={statusCode}
              onRefresh={onRefresh}
              data={data}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              toggleFeatureScopeOne={toggleFeatureScopeOne}
              forSummary={true}
            />
          </Card.Content>
          <Card.Content className="kpi-content-container siteview-progressbar">
            <CarbonEmmisionHOCWithLoader
              loading={isLoadingPerf}
              error={errorInfoPerf}
              statusCode={statusCodePerf}
              onRefresh={onRefreshPerf}
              data={dataPerf}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              toggleFeatureScopeOne={toggleFeatureScopeOne}
              forGraph={true}
            />
          </Card.Content>
          <Card.Content className="kpi-content-container siteview-progressbar">
            <CarbonEmmisionHOCWithLoader
              loading={loadingTable}
              error={errorTable}
              statusCode={statusCodeTable}
              onRefresh={onRefreshTable}
              data={tableData}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              toggleFeatureScopeOne={toggleFeatureScopeOne}
              forTable={true}
            />
          </Card.Content>
        </>
      )}
    </>
  );
};
export default CarbonEmissionHOCSiteSeparate;
